// src/components/LicenseCategoryDropdown.js
import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  FlatList,
  StyleSheet,
  Pressable,
} from 'react-native';
import { useTranslation } from '../i18n';

const COLORS = {
  primary: '#14B8A6',
  background: '#F7F9FA',
  card: '#FFFFFF',
  textDark: '#1C1C1E',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  overlay: 'rgba(0,0,0,0.5)',
};

const LICENSE_CATEGORIES = ['A', 'B', 'C', 'D', 'E'];

const LicenseCategoryDropdown = ({ value, onSelect, editable = true }) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);

  const handleSelect = (category) => {
    onSelect(category);
    setModalVisible(false);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        {t('driverManager.fields.licenseCategory')}
      </Text>
      
      <TouchableOpacity
        style={[styles.dropdown, !editable && styles.disabled]}
        onPress={() => editable && setModalVisible(true)}
        disabled={!editable}
      >
        <Text style={[styles.dropdownText, !value && styles.placeholder]}>
          {value || t('driverManager.fields.selectCategory')}
        </Text>
        <Text style={styles.dropdownIcon}>▼</Text>
      </TouchableOpacity>

      <Modal
        animationType="slide"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <Pressable 
          style={styles.modalOverlay}
          onPress={() => setModalVisible(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('driverManager.fields.selectCategory')}
              </Text>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Text style={styles.closeButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <FlatList
              data={LICENSE_CATEGORIES}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.categoryItem,
                    value === item && styles.categoryItemSelected,
                  ]}
                  onPress={() => handleSelect(item)}
                >
                  <Text
                    style={[
                      styles.categoryText,
                      value === item && styles.categoryTextSelected,
                    ]}
                  >
                    {item}
                  </Text>
                  {value === item && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
                </TouchableOpacity>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
            />
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.textMuted,
    marginBottom: 4,
  },
  dropdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    padding: 12,
    backgroundColor: COLORS.background,
  },
  dropdownText: {
    fontSize: 14,
    color: COLORS.textDark,
  },
  placeholder: {
    color: COLORS.textMuted,
    fontStyle: 'italic',
  },
  dropdownIcon: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  disabled: {
    opacity: 0.5,
    backgroundColor: '#F3F4F6',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: COLORS.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.textDark,
  },
  closeButton: {
    fontSize: 20,
    color: COLORS.textMuted,
    padding: 4,
  },
  categoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  categoryItemSelected: {
    backgroundColor: COLORS.primary + '10',
  },
  categoryText: {
    fontSize: 16,
    color: COLORS.textDark,
    fontWeight: '500',
  },
  categoryTextSelected: {
    color: COLORS.primary,
    fontWeight: '700',
  },
  checkmark: {
    fontSize: 16,
    color: COLORS.primary,
    fontWeight: '700',
  },
  separator: {
    height: 1,
    backgroundColor: COLORS.border,
    marginLeft: 16,
  },
});

export default LicenseCategoryDropdown;